/**
 * PixelAvatar primitive (Plan 01-16, Task 3).
 *
 * 16×16 pixel-grid SVG portrait keyed by colour slot. The slot drives
 * the background fill via `var(--color-m-<slot>)`, so a future rebrand
 * (or a per-member palette tweak in admin settings) is a single CSS
 * token change with no React rerender.
 *
 * `image-rendering: pixelated` preserves the chunky pixel aesthetic at
 * any display size — UI-SPEC §"Image rendering" pins this. The SVG
 * itself is hand-authored to match the warm-Scandinavian face the
 * Stell design handoff ships (kind eyes + small smile + hair tuft);
 * exact handoff coordinates are reproduced below. The face uses
 * locked colours (ink + sheet + mørk primary) so the same portrait
 * looks consistent across every member's chosen background slot.
 *
 * The wrapping `pixel-avatar` class lets `app.css` apply
 * `image-rendering: pixelated` and any future portrait-only rules
 * without the SVG depending on Tailwind utilities.
 *
 * Props:
 *   colorSlot — one of the six `m-*` palette keys (FND-04 — slots, not
 *               names; admin picks one per member during onboarding).
 *   size      — output px; default 64. Visible pixel = size / 16.
 */
export type ColorSlot =
  | 'm-terracotta'
  | 'm-berry'
  | 'm-ochre'
  | 'm-sage'
  | 'm-sky'
  | 'm-plum';

interface PixelAvatarProps {
  colorSlot: ColorSlot;
  size?: number;
  className?: string;
}

// Locked face colours — same for every member portrait.
const INK = 'var(--color-ink)';
const FACE = 'var(--color-sheet)';
// Håret er primary mørknet, samme blanding som det gamle `terracotta-deep`
// hadde før pigmentnavnene ble fjernet (#161).
const HAIR = 'color-mix(in oklab, var(--color-primary) 82%, black)';
const CHEEK = 'var(--color-lime-bg)';

export function PixelAvatar({
  colorSlot,
  size = 64,
  className,
}: PixelAvatarProps) {
  const bg = `var(--color-${colorSlot})`;
  const cls = ['pixel-avatar', className].filter(Boolean).join(' ');
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      shapeRendering="crispEdges"
      style={{ imageRendering: 'pixelated' }}
      className={cls}
      role="img"
      aria-label={colorSlot}
    >
      {/* Background tile (whole 16×16) */}
      <rect x="0" y="0" width="16" height="16" fill={bg} />

      {/* Hair (top + sides) */}
      <rect x="3" y="2" width="10" height="2" fill={HAIR} />
      <rect x="2" y="3" width="2" height="4" fill={HAIR} />
      <rect x="12" y="3" width="2" height="4" fill={HAIR} />
      {/* Hair tuft */}
      <rect x="5" y="1" width="2" height="1" fill={HAIR} />
      <rect x="9" y="1" width="2" height="1" fill={HAIR} />

      {/* Face (skin, linen warmth) */}
      <rect x="4" y="4" width="8" height="7" fill={FACE} />
      <rect x="3" y="5" width="1" height="4" fill={FACE} />
      <rect x="12" y="5" width="1" height="4" fill={FACE} />

      {/* Eyes — ink, single pixel */}
      <rect x="6" y="7" width="1" height="1" fill={INK} />
      <rect x="9" y="7" width="1" height="1" fill={INK} />

      {/* Cheeks — lime-bg */}
      <rect x="5" y="9" width="1" height="1" fill={CHEEK} />
      <rect x="10" y="9" width="1" height="1" fill={CHEEK} />

      {/* Smile — two pixels */}
      <rect x="7" y="9" width="2" height="1" fill={INK} />

      {/* Neck + shoulders (bg-tinted, slightly darker) */}
      <rect x="6" y="11" width="4" height="1" fill={FACE} />
      <rect x="3" y="12" width="10" height="4" fill={bg} />
      <rect x="5" y="13" width="6" height="3" fill={INK} opacity="0.06" />
    </svg>
  );
}
